import WsClient, {DISCONNECTED_CAUSE} from "../ws/WsClient";
import {useContext, useEffect} from "react";
import AppContext from "../context/AppContext";
import {TranslationConferenceTokenData, UserFromToken, WsResponse} from "@inpriton/types";
import {Device} from "mediasoup-client";
import type {types} from "mediasoup-client";
import type {
  CloseConsumerRequest, CloseConsumerResponse,
  CloseTransportsRequest, CloseTransportsResponse,
  ConnectTransportRequest, ConnectTransportResponse, ConsumeRequest, ConsumeResponse,
  CreateTransportRequest,
  CreateTransportResponse,
  DisableTrackRequest, ProduceRequest, ProductResponse,
  StreamType,
  TrackType
} from "@inpriton/types/webrtcTypes";
import {jwtDecode} from "jwt-decode";

class WebRtcClient {
  private ws: WsClient;

  // @ts-ignore
  private device: Device;
  private initialized: boolean = false
  private room: string;

  // RISKS.md/P2.61: ONE receive transport for the whole session, not one per remote track.
  private recvTransport: types.Transport | null = null
  private recvTransportPending: Promise<types.Transport> | null = null
  private consumers = new Map<string, types.Consumer>()

  // RISKS.md/P2.61 + P1.24: WsClient.send QUEUES while the socket is down, so a consume parked
  // mid-negotiation resumes onto the NEXT socket. Without this counter it would overwrite the live
  // consumer of a peer whose producer id never changed, leaving that one with no owner.
  private generation = 0

  // RISKS.md/P1.26: one send transport per (streamType, trackType), superseded once its
  // replacement has produced (RISKS.md/P2.60).
  private sendTransports = new Map<string, types.Transport>()

  // RISKS.md/P1.24: who currently owns publishing for a `${streamType}-${trackType}` key. ⚠ EVERY
  // operation that changes what should be published for a key must replace or drop its entry, or a
  // call parked mid-negotiation resumes and produces a track the app has moved on from — and since
  // both sides supersede LAST-to-produce, that stale publish beats the live one.
  private publishTokens = new Map<string, object>()

  constructor(ws: WsClient, room: string) {
    this.ws = ws
    this.room = room
  }

  setWs(ws: WsClient) {
    this.ws = ws
    const tokenData = jwtDecode(ws.token as string) as TranslationConferenceTokenData
    console.log('webrtc', 'my uuid', tokenData.user.uuid)
  }

  setRoom(room: string) {
    this.room = room
  }

  async init() {
    const resCapabilities = await this.ws.send<any, WsResponse & { data: any }>({
      action: 'getRouterRtpCapabilities',
    })

    this.device = new Device();
    await this.device.load({ routerRtpCapabilities: resCapabilities.data });

    this.initialized = true
  }

  private closeSendTransport(streamType: StreamType, trackType: TrackType) {
    const key = `${streamType}-${trackType}`
    const transport = this.sendTransports.get(key)
    if (!transport) return
    this.sendTransports.delete(key)
    transport.close()
  }

  async disableTrack(streamType: StreamType, trackType: TrackType) {
    console.log('disabling track', streamType, trackType)
    this.publishTokens.delete(`${streamType}-${trackType}`)
    this.closeSendTransport(streamType, trackType)
    // RISKS.md/P1.3: the server's disableWebRtcTrack success path sends no ack, so awaiting a
    // reply hung forever (and would now reject at disconnect) — fire-and-forget. If P1.10 adds
    // a server ack, drop the flag here.
    await this.ws?.send<DisableTrackRequest>({
      action: 'disableWebRtcTrack',
      data: {
        streamType,
        trackType,
      }
    }, false)
  }

  async closeTransports(data: CloseTransportsRequest['data'] = {}) {
    console.log('closing transports', data)
    // RISKS.md/P1.1: typed against the server's transportsClosed ack (the action is now routed).
    return this.ws?.send<CloseTransportsRequest, CloseTransportsResponse>({
      action: "closeTransports",
      data,
    })
  }

  // RISKS.md/P1.13 + P2.61: give back the CONSUMER, never the transport — that one is the
  // session's, not this track's. The caller must drop its `trackIds` entry too, or `applyStreams`
  // will never re-consume it.
  closeStream(producerId: string, trackType: TrackType) {
    const key = this.consumerKey(producerId, trackType)
    const consumer = this.consumers.get(key)
    if (!consumer) return
    this.consumers.delete(key)
    const consumerId = consumer.id
    consumer.close()
    void this.closeConsumer(consumerId)
  }

  // By id and unconditional: the server matches ids against THIS socket, so one queued onto the
  // socket that replaces this one closes nothing there (RISKS.md/P1.13).
  private closeConsumer(consumerId: string) {
    return this.ws?.send<CloseConsumerRequest, CloseConsumerResponse>({
      action: 'closeConsumer',
      data: { consumerId },
    }).catch(e => console.warn('closing a consumer failed:', e))
  }

  // RISKS.md/P1.13: this client is a module-level singleton that outlives any one socket or room,
  // so every socket change must return it to its initial state — including `initialized`, or the
  // next session negotiates against a Device loaded from the previous router's capabilities.
  // ⚠ Client-side ONLY, deliberately: WsClient.send() QUEUES while the socket is down, so a
  // closeTransports from here would arrive on the NEXT socket and close the transports it has just
  // created. The server drops these with the socket anyway.
  reset() {
    const close = (transport: types.Transport) => {
      try { transport.close() } catch (e) { /* already closed */ }
    }
    // RISKS.md/P2.61: bump FIRST, so anything still negotiating abandons rather than landing on
    // the transport this is about to close, or on the one the next socket creates.
    this.generation++
    if (this.recvTransport) close(this.recvTransport)
    this.recvTransport = null
    this.recvTransportPending = null
    this.consumers.clear()
    this.sendTransports.forEach(close)
    this.sendTransports.clear()
    // RISKS.md/P1.24: a publish still negotiating on the dead socket must not resume onto the new one.
    this.publishTokens.clear()
    this.initialized = false
  }

  private consumerKey(producerId: string, trackType: TrackType) {
    return `${producerId}-${trackType}`
  }

  // Single-flight: a re-join runs updateStreams for every peer at once, so the first consumes race
  // each other and would otherwise open a transport apiece.
  private async ensureRecvTransport(generation: number): Promise<types.Transport> {
    if (this.recvTransport) return this.recvTransport
    if (!this.recvTransportPending) {
      this.recvTransportPending = (async () => {
        const res = await this.ws.send<CreateTransportRequest, CreateTransportResponse>({
          action: 'createWebRtcTransport',
          data: { type: 'receive' },
        })

        if (this.generation !== generation) {
          void this.closeTransports({ transportIds: [res.data.id] })
            .catch(e => console.warn('discarding an abandoned receive transport failed:', e))
          throw new Error('websocket disconnected', { cause: DISCONNECTED_CAUSE })
        }

        const recvTransport = this.device.createRecvTransport(res.data)

        recvTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
          this.ws.send<ConnectTransportRequest, ConnectTransportResponse>({
            action: 'connectTransport',
            data: {
              transportId: res.data.id,
              dtlsParameters,
            },
          }).then(callback).catch(errback);
        });

        this.recvTransport = recvTransport
        return recvTransport
      })().finally(() => { this.recvTransportPending = null })
    }
    return this.recvTransportPending
  }

  isInitialized() {
    return this.initialized
  }

  // RISKS.md/P1.24: the track types this call still owns; one a later publish took over is excluded.
  async createSender(readTracks: () => MediaStream, streamType: StreamType): Promise<TrackType[]> {
    if (!this.initialized) {
      await this.init()
    }

    const kinds: TrackType[] = ['audio', 'video']
    const superseded = new Set<TrackType>()
    for (const kind of kinds) {
      // RISKS.md/P1.36: read each kind only when its turn comes, and pass it to enableTrack with no
      // await in between: enableTrack claims the kind's token before its first await. The capture
      // paths publish a new track themselves once `initialized` is set, so an earlier read can be
      // stale: it misses a track captured during init(), or returns the raw camera a filter swapped
      // out meanwhile, still live as the filter's input, which would supersede the filtered one.
      for (const track of readTracks().getTracks().filter(t => t.kind === kind)) {
        // RISKS.md/P1.23: enableTrack refuses an ended track too, but skipping it here counts its
        // kind as owned rather than superseded, so publishLocalMedia clears that kind's failure.
        if (track.readyState !== 'live') {
          console.warn('skipping ended track:', track.kind)
          continue
        }
        if (!await this.enableTrack(track, streamType)) superseded.add(kind)
      }
    }
    return kinds.filter(kind => !superseded.has(kind))
  }

  // Resolves true when THIS call published. False is not a failure: a later publish for the same
  // key, or a track the app has since stopped, owns the outcome instead (RISKS.md/P1.24).
  async enableTrack(track: MediaStreamTrack, streamType: StreamType): Promise<boolean> {
    const trackType = track.kind as TrackType
    const key = `${streamType}-${trackType}`
    const token = {}
    this.publishTokens.set(key, token)
    const owns = () => this.publishTokens.get(key) === token && track.readyState === 'live'

    // RISKS.md/P2.60: the previous sender keeps publishing until this one has produced — closing it
    // here starves peers for the two round trips in between. It is closed after `produce` below,
    // which is also when the server supersedes it, so two overlapping calls for one key resolve the
    // same way on both sides.
    const resSendTransport = await this.ws.send<CreateTransportRequest, CreateTransportResponse>({
      action: 'createWebRtcTransport',
      data: {
        type: 'send',
        streamType,
        trackType,
      },
    });

    if (!owns()) {
      this.discardSender(resSendTransport.data.id)
      return false
    }

    const sendTransport = this.device.createSendTransport(resSendTransport.data);

    sendTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
      this.ws.send<ConnectTransportRequest, ConnectTransportResponse>({
        action: 'connectTransport',
        data: {
          transportId: resSendTransport.data.id,
          dtlsParameters,
        },
      }).then(callback).catch(errback);
    });

    let superseded = false
    sendTransport.on('produce', async ({ kind, rtpParameters, appData }, callback, errback) => {
      if (!owns()) {
        // errback, NOT a bare return or a close: mediasoup-client settles this handler's own promise
        // through these two callbacks only, so calling neither leaves `produce` below pending for
        // good — the very hang this guard exists to contain (RISKS.md/P1.24).
        superseded = true
        errback(new Error('superseded'))
        return
      }
      try {
        const res = await this.ws.send<ProduceRequest, ProductResponse>({
          action: 'produce',
          data: {
            transportId: resSendTransport.data.id,
            kind,
            rtpParameters,
          },
        });
        console.log('created producer', track.kind, res.data.producerId)
        callback({ id: res.data.producerId }); // important: call callback with producer id
      } catch (err: any) {
        errback(err);
      }
    });

    try {
      await sendTransport.produce({
        track,
        // Closing the transport closes its producer, and mediasoup-client then STOPS the producer's
        // track unless told not to. That track is the raw camera still feeding the background
        // filter and the green-room preview, or a working microphone whose re-capture failed —
        // and stop() fires no `ended`, so nothing would recover it (RISKS.md/P1.26).
        stopTracks: false,
        encodings: [{ maxBitrate: 1500000 }],
        codecOptions: {},
      });
    } catch (e) {
      sendTransport.close()
      // Failed as well as abandoned: the server keeps the transport until it is handed back, and
      // each Retry would stack another until the per-socket cap refuses every publish
      // (RISKS.md/P1.38).
      this.discardSender(resSendTransport.data.id)
      if (superseded || !owns()) {
        return false
      }
      throw e
    }

    if (!owns()) {
      // A produce that landed after the app moved on. Closing it rather than storing it leaves the
      // map naming the previous sender, still publishing — the state P2.60 wants.
      sendTransport.close()
      this.discardSender(resSendTransport.data.id)
      return false
    }

    const previous = this.sendTransports.get(key)
    this.sendTransports.set(key, sendTransport)
    if (previous && previous !== sendTransport) {
      previous.close()
    }
    return true
  }

  // Hand the server end of an abandoned sender back. Unconditional, like closeStream: the server
  // filters by socket and acks an id it does not know, while gating on `initialized` would leak the
  // transport in the commonest case, an abort right after reset() (RISKS.md/P1.24).
  private discardSender(transportId: string) {
    void this.closeTransports({ transportIds: [transportId] })
      .catch(e => console.warn('discarding a superseded sender failed:', e))
  }

  async getStream(producerId: string, streamType: StreamType, trackType: TrackType): Promise<MediaStream> {
    const generation = this.generation
    // A stale call rejects as `disconnected`, which applyStreams knows not to report and not to
    // record as a consume failure.
    const abandoned = () => this.generation !== generation
    const abandon = () => new Error('websocket disconnected', { cause: DISCONNECTED_CAUSE })

    if (!this.initialized) {
      await this.init()
    }
    if (abandoned()) throw abandon()

    console.log('requesting track', producerId)

    const recvTransport = await this.ensureRecvTransport(generation)
    if (abandoned()) throw abandon()

    const resConsumerInfo = await this.ws.send<ConsumeRequest, ConsumeResponse>({
      action: 'consume',
      data: {
        transportId: recvTransport.id,
        producerId: producerId,
        rtpCapabilities: this.device.recvRtpCapabilities
      },
    });

    const consumerInfo = resConsumerInfo.data

    if (abandoned()) {
      void this.closeConsumer(consumerInfo.consumerId)
      throw abandon()
    }

    const consumer = await recvTransport.consume({
      id: consumerInfo.consumerId,
      producerId: consumerInfo.producerId,
      kind: consumerInfo.kind,
      rtpParameters: consumerInfo.rtpParameters,
    }).catch(e => {
      // The server built its consumer before this failed and only the success path below records
      // one, so nothing downstream can give it back (RISKS.md/P2.61).
      void this.closeConsumer(consumerInfo.consumerId)
      throw e
    });

    if (abandoned()) {
      consumer.close()
      void this.closeConsumer(consumer.id)
      throw abandon()
    }

    this.consumers.set(this.consumerKey(producerId, trackType), consumer)

    const remoteStream = new MediaStream();
    remoteStream.addTrack(consumer.track);

    return remoteStream
  }
}

let client: WebRtcClient | null

const useWebRtcClient = (): WebRtcClient => {
  const {
    ws,
    currentRoom,
  } = useContext(AppContext)

  if (!client) {
    client = new WebRtcClient(ws as WsClient, currentRoom as string)
  }

  useEffect(() => {
    if (ws) {
      client?.setWs(ws)
    }
  }, [ws]);

  useEffect(() => {
    if (currentRoom) {
      client?.setRoom(currentRoom)
    }
  }, [currentRoom]);

  return client
}

export {
  useWebRtcClient as default,
  WebRtcClient,
}